import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@nextui-org/react';
import { Icon } from '@iconify/react';
import { toast } from 'react-toastify';
import { ExporterReceiver, Species } from 'common/src/utils/types';
import { getExporterSpecies } from 'common/src/api/species';
import { SEASONS } from 'common/src/utils/definitions';
import {
  getReceiverAccessUsers,
  inviteReceiverUser,
  ReceiverAccessScope,
  ReceiverAccessUser,
  setReceiverAccessActive,
  updateReceiverAccessScopes,
} from 'common/src/api/receiver-access';
import { LoadingState } from '../ui';

interface ClientUsersModalProps {
  isOpen: boolean;
  onClose: () => void;
  receiver: ExporterReceiver | null;
  onUsersLoaded?: (receiverId: number, count: number) => void;
}

export const ClientUsersModal = ({
  isOpen,
  onClose,
  receiver,
  onUsersLoaded,
}: ClientUsersModalProps) => {
  const [users, setUsers] = useState<ReceiverAccessUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [availableSpecies, setAvailableSpecies] = useState<Species[]>([]);
  const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
  const [selectedSpeciesIds, setSelectedSpeciesIds] = useState<string[]>([]);
  const [editingAccessId, setEditingAccessId] = useState<number | null>(null);
  const [editingSeasons, setEditingSeasons] = useState<string[]>([]);
  const [editingSpeciesIds, setEditingSpeciesIds] = useState<string[]>([]);
  const [savingScopes, setSavingScopes] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  const receiverAccessBenefits = [
    {
      title: 'Acceso a sus embarques',
      description:
        'El cliente puede revisar el estado de sus embarques sin pedir actualizaciones manuales al equipo comercial.',
      icon: 'tabler:ship',
      accent: 'bg-blue-50 text-blue-700 border-blue-100',
    },
    {
      title: 'Acceso a su programa comercial',
      description:
        'Puede consultar sus compromisos, temporadas y especies autorizadas desde un solo portal.',
      icon: 'tabler:briefcase',
      accent: 'bg-emerald-50 text-emerald-700 border-emerald-100',
    },
    {
      title: 'Próximamente: liquidación a través del portal',
      description:
        'El mismo acceso permitirá centralizar liquidaciones y reducir envíos dispersos por correo.',
      icon: 'tabler:file-invoice',
      accent: 'bg-amber-50 text-amber-700 border-amber-100',
    },
    {
      title: 'Más funcionalidades',
      description:
        'Seguiremos sumando vistas y acciones para que el cliente trabaje directamente desde CherryMarket / FruitMarkets.',
      icon: 'tabler:sparkles',
      accent: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    },
  ];

  const seasonOptions = useMemo(
    () => Object.values(SEASONS).map((season) => season.name),
    []
  );

  const speciesNameById = useMemo(() => {
    const map = new Map<number, string>();
    availableSpecies.forEach((species) => map.set(species.id, species.name));
    return map;
  }, [availableSpecies]);

  const editingUser = useMemo(
    () => users.find((user) => user.id === editingAccessId) || null,
    [editingAccessId, users]
  );

  const selectionToArray = (keys: 'all' | Set<Key>) => {
    if (keys === 'all') return [];
    return Array.from(keys).map(String);
  };

  const buildScopes = (
    seasons: string[],
    speciesIds: string[]
  ): ReceiverAccessScope[] =>
    seasons.flatMap((season) =>
      speciesIds.map((speciesId) => ({
        season,
        species_id: Number(speciesId),
      }))
    );

  const describeScopes = (scopes: ReceiverAccessScope[]) => {
    if (!scopes.length) return 'Sin permisos asignados';
    const seasons = Array.from(new Set(scopes.map((scope) => scope.season)));
    const speciesNames = Array.from(
      new Set(
        scopes.map(
          (scope) =>
            scope.species?.name ||
            speciesNameById.get(scope.species_id) ||
            `Especie ${scope.species_id}`
        )
      )
    );
    return `${seasons.join(', ')} · ${speciesNames.join(', ')}`;
  };

  const fetchUsers = useCallback(async () => {
    if (!receiver?.id) return;
    setLoading(true);
    try {
      const rows = await getReceiverAccessUsers(receiver.id);
      setUsers(rows);
      onUsersLoaded?.(receiver.id, rows.length);
    } catch (error) {
      console.error('Error loading receiver users:', error);
      toast.error('Error al cargar usuarios del cliente.');
    } finally {
      setLoading(false);
    }
  }, [onUsersLoaded, receiver?.id]);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
    }
  }, [fetchUsers, isOpen]);

  useEffect(() => {
    const fetchSpecies = async () => {
      if (!isOpen || !receiver?.exporter_id) {
        setAvailableSpecies([]);
        return;
      }

      try {
        const species = await getExporterSpecies(receiver.exporter_id);
        setAvailableSpecies(species);
      } catch (error) {
        console.error('Error loading exporter species:', error);
        toast.error('Error al cargar especies de la exportadora.');
      }
    };

    fetchSpecies();
  }, [isOpen, receiver?.exporter_id]);

  const resetForm = () => {
    setEmail('');
    setFirstName('');
    setLastName('');
    setSelectedSeasons([]);
    setSelectedSpeciesIds([]);
  };

  const handleClose = () => {
    resetForm();
    setUsers([]);
    setAvailableSpecies([]);
    setEditingAccessId(null);
    setIsInviteOpen(false);
    setIsHowItWorksOpen(false);
    onClose();
  };

  const closeInviteModal = () => {
    resetForm();
    setIsInviteOpen(false);
  };

  const handleInvite = async () => {
    if (!receiver?.id || !email.trim()) {
      toast.warn('Ingresa un email.');
      return;
    }

    const scopes = buildScopes(selectedSeasons, selectedSpeciesIds);
    if (scopes.length === 0) {
      toast.warn('Selecciona al menos una temporada y una especie.');
      return;
    }

    setSaving(true);
    try {
      const result = await inviteReceiverUser({
        exporterReceiverId: receiver.id,
        email: email.trim(),
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        scopes,
      });
      toast.success(
        result.email_sent
          ? 'Usuario invitado con éxito.'
          : 'Acceso creado. No se pudo enviar el correo.'
      );
      resetForm();
      setIsInviteOpen(false);
      await fetchUsers();
    } catch (error) {
      console.error('Error inviting receiver user:', error);
      toast.error(
        error instanceof Error ? error.message : 'Error al invitar usuario.'
      );
    } finally {
      setSaving(false);
    }
  };

  const startEditingScopes = (user: ReceiverAccessUser) => {
    const scopes = user.scopes || [];
    setEditingAccessId(user.id);
    setEditingSeasons(Array.from(new Set(scopes.map((scope) => scope.season))));
    setEditingSpeciesIds(
      Array.from(new Set(scopes.map((scope) => String(scope.species_id))))
    );
  };

  const handleSaveScopes = async (accessId: number) => {
    const scopes = buildScopes(editingSeasons, editingSpeciesIds);
    if (scopes.length === 0) {
      toast.warn('Selecciona al menos una temporada y una especie.');
      return;
    }

    setSavingScopes(true);
    try {
      await updateReceiverAccessScopes(accessId, scopes);
      toast.success('Permisos actualizados.');
      setEditingAccessId(null);
      await fetchUsers();
    } catch (error) {
      console.error('Error updating receiver scopes:', error);
      toast.error('Error al actualizar permisos.');
    } finally {
      setSavingScopes(false);
    }
  };

  const handleSetActive = async (accessId: number, isActive: boolean) => {
    try {
      await setReceiverAccessActive(accessId, isActive);
      toast.success(isActive ? 'Acceso activado.' : 'Acceso desactivado.');
      await fetchUsers();
    } catch (error) {
      console.error('Error updating receiver access:', error);
      toast.error('Error al actualizar el acceso.');
    }
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={handleClose} size='5xl'>
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                Usuarios de {receiver?.name || 'cliente'}
              </ModalHeader>
              <ModalBody>
                <div className='flex flex-col sm:flex-row sm:justify-end gap-2'>
                  <Button
                    variant='flat'
                    startContent={<Icon icon='tabler:help-circle' width={18} />}
                    onPress={() => setIsHowItWorksOpen(true)}
                  >
                    Cómo funciona
                  </Button>
                  <Button color='primary' onPress={() => setIsInviteOpen(true)}>
                    Invitar Usuario
                  </Button>
                </div>

                {loading ? (
                  <LoadingState />
                ) : (
                  <Table aria-label='Usuarios con acceso al cliente'>
                    <TableHeader>
                      <TableColumn>USUARIO</TableColumn>
                      <TableColumn>EMAIL</TableColumn>
                      <TableColumn>PERMISOS</TableColumn>
                      <TableColumn>ESTADO</TableColumn>
                      <TableColumn>ACCIONES</TableColumn>
                    </TableHeader>
                    <TableBody
                      emptyContent='Sin usuarios invitados.'
                      items={users}
                    >
                      {(user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {[user.first_name, user.last_name]
                              .filter(Boolean)
                              .join(' ') || '-'}
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          <TableCell>
                            <span className='text-sm text-default-600'>
                              {describeScopes(user.scopes || [])}
                            </span>
                          </TableCell>
                          <TableCell>
                            <Chip
                              color={user.is_active ? 'success' : 'default'}
                              variant='flat'
                            >
                              {user.is_active ? 'Activo' : 'Inactivo'}
                            </Chip>
                          </TableCell>
                          <TableCell>
                            <div className='flex flex-col gap-2'>
                              <Button
                                size='sm'
                                variant='flat'
                                onPress={() => startEditingScopes(user)}
                              >
                                Editar permisos
                              </Button>
                              <Button
                                size='sm'
                                variant='flat'
                                color={user.is_active ? 'danger' : 'success'}
                                onPress={() =>
                                  handleSetActive(user.id, !user.is_active)
                                }
                              >
                                {user.is_active ? 'Desactivar' : 'Activar'}
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={handleClose}>
                  Cerrar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal isOpen={isInviteOpen} onClose={closeInviteModal} size='2xl'>
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Invitar Usuario</ModalHeader>
              <ModalBody>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
                  <Input
                    label='Email'
                    type='email'
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                  <Input
                    label='Nombre'
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                  />
                  <Input
                    label='Apellido'
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                  />
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  <Select
                    label='Temporadas'
                    selectionMode='multiple'
                    selectedKeys={new Set(selectedSeasons)}
                    onSelectionChange={(keys) =>
                      setSelectedSeasons(
                        selectionToArray(keys as 'all' | Set<Key>)
                      )
                    }
                  >
                    {seasonOptions.map((season) => (
                      <SelectItem key={season}>{season}</SelectItem>
                    ))}
                  </Select>
                  <Select
                    label='Especies'
                    selectionMode='multiple'
                    selectedKeys={new Set(selectedSpeciesIds)}
                    onSelectionChange={(keys) =>
                      setSelectedSpeciesIds(
                        selectionToArray(keys as 'all' | Set<Key>)
                      )
                    }
                  >
                    {availableSpecies.map((species) => (
                      <SelectItem key={String(species.id)}>
                        {species.name}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button variant='light' onPress={closeInviteModal}>
                  Cancelar
                </Button>
                <Button
                  color='primary'
                  onPress={handleInvite}
                  isLoading={saving}
                >
                  Invitar Usuario
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={isHowItWorksOpen}
        onClose={() => setIsHowItWorksOpen(false)}
        size='3xl'
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>
                <div>
                  <p className='text-xl font-semibold text-foreground'>
                    Cómo funciona
                  </p>
                  <p className='text-sm font-normal text-default-500'>
                    Por ahora, estos accesos permiten abrir el portal a usuarios
                    del cliente con permisos controlados por temporada y
                    especie.
                  </p>
                </div>
              </ModalHeader>
              <ModalBody>
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                  {receiverAccessBenefits.map((benefit, index) => (
                    <div
                      key={benefit.title}
                      className='rounded-medium border border-default-200 p-4'
                    >
                      <div className='flex items-start gap-3'>
                        <div
                          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border ${benefit.accent}`}
                        >
                          <Icon icon={benefit.icon} width={22} />
                        </div>
                        <div className='min-w-0'>
                          <div className='mb-1 flex items-center gap-2'>
                            <span className='flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-default-100 text-xs font-semibold text-default-700'>
                              {index + 1}
                            </span>
                            <h3 className='text-sm font-semibold text-foreground'>
                              {benefit.title}
                            </h3>
                          </div>
                          <p className='text-sm leading-5 text-default-600'>
                            {benefit.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ModalBody>
              <ModalFooter>
                <Button
                  color='primary'
                  onPress={() => setIsHowItWorksOpen(false)}
                >
                  Entendido
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>

      <Modal
        isOpen={Boolean(editingUser)}
        onClose={() => setEditingAccessId(null)}
        size='2xl'
      >
        <ModalContent>
          {() => (
            <>
              <ModalHeader>Editar permisos de {editingUser?.email}</ModalHeader>
              <ModalBody>
                <Select
                  label='Temporadas'
                  selectionMode='multiple'
                  selectedKeys={new Set(editingSeasons)}
                  onSelectionChange={(keys) =>
                    setEditingSeasons(
                      selectionToArray(keys as 'all' | Set<Key>)
                    )
                  }
                >
                  {seasonOptions.map((season) => (
                    <SelectItem key={season}>{season}</SelectItem>
                  ))}
                </Select>
                <Select
                  label='Especies'
                  selectionMode='multiple'
                  selectedKeys={new Set(editingSpeciesIds)}
                  onSelectionChange={(keys) =>
                    setEditingSpeciesIds(
                      selectionToArray(keys as 'all' | Set<Key>)
                    )
                  }
                >
                  {availableSpecies.map((species) => (
                    <SelectItem key={String(species.id)}>
                      {species.name}
                    </SelectItem>
                  ))}
                </Select>
              </ModalBody>
              <ModalFooter>
                <Button
                  variant='light'
                  onPress={() => setEditingAccessId(null)}
                >
                  Cancelar
                </Button>
                <Button
                  color='primary'
                  isLoading={savingScopes}
                  onPress={() =>
                    editingUser ? handleSaveScopes(editingUser.id) : undefined
                  }
                >
                  Guardar
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
